/* Shreya — header: sticky states, transparent mode, desktop menus, menu drawer */
(() => {
  if (customElements.get('site-header')) return;

  const root = document.documentElement;
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  /* ------------------------------------------------------------------ */
  /* <site-header>                                                       */
  /* ------------------------------------------------------------------ */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.stickyMode = this.dataset.sticky || 'none';
      this.openMode = this.dataset.menuOpen || 'hover';
      this.isTransparent = this.hasAttribute('data-transparent');
      this.lastY = window.scrollY;
      this.items = Array.from(this.querySelectorAll('[data-nav-item]'));

      this.liftDrawer();
      this.bindMenus();

      this.onScroll = Theme.throttle(() => this.updateScroll());
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this);
      this.onResize = Theme.debounce(() => { this.measure(); this.updateScroll(); }, 120);
      window.addEventListener('resize', this.onResize);

      this.onDocClick = (e) => { if (!this.contains(e.target)) this.closeAll(); };
      document.addEventListener('click', this.onDocClick);

      this.onKey = (e) => {
        if (e.key !== 'Escape') return;
        const open = this.items.find((item) => item.classList.contains('is-open'));
        if (open) {
          this.close(open);
          open.querySelector('[data-nav-toggle]')?.focus();
        }
      };
      this.addEventListener('keydown', this.onKey);

      this.onDrawerChange = (e) => {
        const id = e.detail && e.detail.id;
        if (id !== 'menu-drawer') { if (e.type === 'theme:drawer:open') this.closeAll(); return; }
        const burger = this.querySelector('.site-header__burger');
        if (burger) burger.setAttribute('aria-expanded', String(e.type === 'theme:drawer:open'));
      };
      document.addEventListener('theme:drawer:open', this.onDrawerChange);
      document.addEventListener('theme:drawer:close', this.onDrawerChange);

      this.onBlockSelect = (e) => this.handleEditorSelect(e, true);
      this.onBlockDeselect = (e) => this.handleEditorSelect(e, false);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.measure();
      this.updateScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('theme:drawer:open', this.onDrawerChange);
      document.removeEventListener('theme:drawer:close', this.onDrawerChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.drawer && this.drawer.parentElement === document.body) this.drawer.remove();
      root.style.setProperty('--header-offset', '0px');
    }

    /* The sticky wrapper forms a stacking context, so the menu drawer is moved to <body>
       to sit above the page overlay like every other drawer. */
    liftDrawer() {
      const drawer = this.section && this.section.querySelector(':scope > drawer-panel#menu-drawer');
      if (!drawer) return;
      document.querySelectorAll('body > drawer-panel#menu-drawer').forEach((old) => old !== drawer && old.remove());
      document.body.appendChild(drawer);
      this.drawer = drawer;
    }

    /* ---------- Sizes & scroll ---------- */
    measure() {
      const h = Math.round(this.getBoundingClientRect().height);
      if (!h) return;
      this.height = h;
      root.style.setProperty('--header-height', `${h}px`);
      this.setOffset();
    }

    naturalTop() {
      const prev = this.section && this.section.previousElementSibling;
      if (!prev) return 0;
      const rect = prev.getBoundingClientRect();
      return Math.max(0, rect.bottom + window.scrollY);
    }

    setOffset() {
      const visible = this.stickyMode !== 'none' && !this.classList.contains('is-hidden');
      root.style.setProperty('--header-offset', visible ? `${this.height || 0}px` : '0px');
    }

    updateScroll() {
      const y = Math.max(0, window.scrollY);
      const top = this.naturalTop();
      const stuck = this.stickyMode !== 'none' && y > top + 1;
      this.classList.toggle('is-stuck', stuck);

      // Transparent headers turn solid once the page moves or a menu is open
      if (this.isTransparent) this.classList.toggle('is-solid', y > top + 4 || this.hasOpenMenu());

      if (this.stickyMode === 'scroll-up') {
        const delta = y - this.lastY;
        const beyond = y > top + (this.height || 0) * 1.5;
        if (delta > 6 && beyond && !this.hasOpenMenu()) this.classList.add('is-hidden');
        else if (delta < -6 || !beyond) this.classList.remove('is-hidden');
        this.setOffset();
      }
      this.lastY = y;
    }

    /* ---------- Desktop menus ---------- */
    bindMenus() {
      this.items.forEach((item) => {
        const link = item.querySelector('[data-nav-link]');
        const toggle = item.querySelector('[data-nav-toggle]');

        toggle?.addEventListener('click', (e) => {
          e.preventDefault();
          // With hover menus a mouse has already opened the panel; keep it open on click
          if (this.openMode === 'hover' && e.detail > 0 && canHover()) {
            this.open(item);
            return;
          }
          this.isOpen(item) ? this.close(item) : this.open(item);
        });

        link?.addEventListener('click', (e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey) return;
          // Click mode, or first tap on touch screens: open the panel instead of navigating
          if (this.openMode === 'click' || !canHover()) {
            if (!this.isOpen(item)) {
              e.preventDefault();
              this.open(item);
            } else if (this.openMode === 'click') {
              e.preventDefault();
              this.close(item);
            }
          }
        });

        if (this.openMode === 'hover') {
          item.addEventListener('pointerenter', (e) => {
            if (e.pointerType !== 'mouse' || !isDesktop()) return;
            clearTimeout(item._closeTimer);
            item._openTimer = setTimeout(() => this.open(item), 70);
          });
          item.addEventListener('pointerleave', (e) => {
            if (e.pointerType !== 'mouse') return;
            clearTimeout(item._openTimer);
            item._closeTimer = setTimeout(() => this.close(item), 160);
          });
        }

        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) this.close(item);
        });
      });
    }

    isOpen(item) { return item.classList.contains('is-open'); }
    hasOpenMenu() { return this.items.some((item) => item.classList.contains('is-open')); }

    open(item) {
      this.items.forEach((other) => other !== item && this.close(other));
      item.classList.add('is-open');
      item.querySelector('[data-nav-toggle]')?.setAttribute('aria-expanded', 'true');
      this.classList.add('has-open-menu');
      if (this.isTransparent) this.classList.add('is-solid');
    }

    close(item) {
      if (!item.classList.contains('is-open')) return;
      item.classList.remove('is-open');
      item.querySelector('[data-nav-toggle]')?.setAttribute('aria-expanded', 'false');
      if (!this.hasOpenMenu()) {
        this.classList.remove('has-open-menu');
        this.updateScroll();
      }
    }

    closeAll() { this.items.forEach((item) => this.close(item)); }

    handleEditorSelect(e, selected) {
      if (!this.section || !this.section.contains(e.target)) return;
      const item = e.target.closest('[data-nav-item]');
      if (item && selected && isDesktop()) this.open(item);
      else if (!selected) this.closeAll();
    }
  }

  /* ------------------------------------------------------------------ */
  /* <menu-drawer-nav> — sliding nested panels inside the menu drawer    */
  /* ------------------------------------------------------------------ */
  class MenuDrawerNav extends HTMLElement {
    connectedCallback() {
      // Element listeners survive the move to <body>, so bind them only once
      if (!this._bound) {
        this._bound = true;
        this.addEventListener('click', (e) => {
          const opener = e.target.closest('[data-submenu-open]');
          if (opener && this.contains(opener)) {
            e.preventDefault();
            this.openSub(opener);
            return;
          }
          const back = e.target.closest('[data-submenu-back]');
          if (back) {
            e.preventDefault();
            this.closeSub(back.closest('[data-submenu]'));
          }
        });
        this.addEventListener('keydown', (e) => {
          // ArrowLeft inside a sub panel goes back one level
          if (e.key !== 'ArrowLeft') return;
          const sub = e.target.closest('[data-submenu].is-active');
          if (sub && !e.target.matches('input, select, textarea')) {
            e.preventDefault();
            this.closeSub(sub);
          }
        });
        this.onDrawerClose = (e) => {
          if (e.detail && e.detail.id === 'menu-drawer') setTimeout(() => this.reset(), 400);
        };
      }
      document.addEventListener('theme:drawer:close', this.onDrawerClose);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:drawer:close', this.onDrawerClose);
    }

    openSub(button) {
      const sub = document.getElementById(button.getAttribute('aria-controls'));
      if (!sub) return;
      const parent = button.closest('.menu-drawer__panel');
      if (parent) {
        parent.scrollTop = 0;
        parent.classList.add('is-covered');
      }
      sub.scrollTop = 0;
      sub.classList.add('is-active');
      button.setAttribute('aria-expanded', 'true');
      const back = sub.querySelector(':scope > [data-submenu-back]');
      if (back) back.focus({ preventScroll: true });
    }

    closeSub(sub) {
      if (!sub) return;
      sub.classList.remove('is-active');
      const parent = sub.parentElement && sub.parentElement.closest('.menu-drawer__panel');
      if (parent) parent.classList.remove('is-covered');
      const button = this.querySelector(`[aria-controls="${sub.id}"]`);
      if (button) {
        button.setAttribute('aria-expanded', 'false');
        button.focus({ preventScroll: true });
      }
    }

    reset() {
      this.querySelectorAll('[data-submenu].is-active').forEach((sub) => sub.classList.remove('is-active'));
      this.querySelectorAll('.is-covered').forEach((panel) => panel.classList.remove('is-covered'));
      this.querySelectorAll('[data-submenu-open]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    }
  }

  customElements.define('site-header', SiteHeader);
  if (!customElements.get('menu-drawer-nav')) customElements.define('menu-drawer-nav', MenuDrawerNav);
})();
